import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useRotaBase from '../../utils/useRotaBase';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';

import ListaCadastroGenerico, { ICampo } from '../../components/ListaCadastroGenerico';
import FichaCadastroGenerico from '../../components/FichaCadastroGenerico';
import Button from '../../components/Button';
import Pagina from '../../components/Pagina';
import CampoPesquisa from '../../components/CampoPesquisa';

import Context, { IContext } from '../../context/Context';
import api from '../../services/api';
import useWindowDimensions from '../../utils/WindowDimensions';
import { ILista } from '../../types/Lista';

// Vendas/compras têm o grid de itens embaixo da lista: ficam com 8 por página, sem escolha
const POR_PAGINA_GRID = 8;
const OPCOES_POR_PAGINA = [10, 25, 50, 100];
const CHAVE_POR_PAGINA = 'sigepeweb::lista:porPagina';

function lerPorPagina(): number {
  try {
    const salvo = Number(localStorage.getItem(CHAVE_POR_PAGINA));
    if (OPCOES_POR_PAGINA.includes(salvo)) return salvo;
  } catch {
    // localStorage bloqueado: usa o padrão
  }
  return OPCOES_POR_PAGINA[0];
}

export default function Cadastro() {
  const navigate = useNavigate();
  const location = useLocation();
  const rotaBase = useRotaBase();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const { setIsLoadingOverlay }: IContext = useContext(Context);
  const { entidade, codigo } = useParams();

  const inputPesquisaRef = useRef<HTMLInputElement>(null);
  const [campos, setCampos] = useState<ICampo[]>([]);
  const [dados, setDados] = useState<ILista | undefined>();
  const [pagina, setPagina] = useState<number>(1);
  const [ordenarPor, setOrdenarPor] = useState<string>('cod desc');
  const [porPagina, setPorPagina] = useState<number>(lerPorPagina);

  const usaGrid = entidade === 'vendas' || entidade === 'compras';

  function alterarPorPagina(novo: number) {
    try {
      localStorage.setItem(CHAVE_POR_PAGINA, String(novo));
    } catch {
      // sem localStorage a escolha vale só até recarregar a página
    }
    setPorPagina(novo);
    setPagina(1);
  }

  async function getDados(entidade?: string) {
    try {
      if (!entidade) return;
      if (pagina < 1) return;
      if (codigo === 'novo') return;

      setIsLoadingOverlay(true);

      let endpoint = entidade === 'produto' ? 'mercador' : entidade;

      switch (entidade) {
        case 'produto':
          endpoint = '/mercador';
          break;
        case 'cliente':
          endpoint = '/cliente';
          break;
        case 'fornecedor':
          endpoint = '/forneced';
          break;
        case 'vendas':
          endpoint = '/pedsai';
          break;
        case 'compras':
          endpoint = '/pedent';
          break;
        default:
          break;
      }

      const response = await api.get(endpoint, {
        params: {
          page: pagina,
          per_page: usaGrid ? POR_PAGINA_GRID : porPagina,
          cod: codigo,
          order: ordenarPor,
          pesquisa: inputPesquisaRef.current?.value,
          ...(entidade === 'produto' && { filtro: '0' })
        }
      });

      if (response.status === 200 || Array.isArray(response.data.data)) {

        if (Array.isArray(response.data?.data) && response.data.data.length >= 1) {
          // Pegando os nomes dos campos direto do retorno da API
          const camposResponse = Object.keys(response.data.data[0]);

          // Listas e objetos (ex.: fotos do mercador) não são colunas nem campos da ficha
          const primeiro = response.data.data[0];
          let camposFiltrados = camposResponse.filter(campo => primeiro[campo] === null || typeof primeiro[campo] !== 'object');

          const campos = camposFiltrados.map((campo) => {
            return {
              label: campo,
              campo
            };
          });

          // Só monta os campos depois do laytel: renderizar antes as colunas cruas registrava no formulário
          // as ocultas (ativo, codsecmer...), com nulo virando 0, e o PUT gravava esses valores
          await getCamposLabel(campos);
        }

        setDados(response.data);
      }
    } catch (error: any) {
      console.log(error.message);
      toast.error(`Erro ao carregar dados. ${entidade}`);
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function getCamposLabel(camposAtuais: ICampo[]) {
    try {
      if (!entidade) return;

      let tabela = entidade === 'produto' ? 'mercador' : entidade;

      switch (entidade) {
        case 'produto':
          tabela = 'mercador';
          break;
        case 'cliente':
          tabela = 'cliente';
          break;
        case 'fornecedor':
          tabela = 'forneced';
          break;
        case 'vendas':
          tabela = 'pedsai';
          break;
        case 'compras':
          tabela = 'pedent';
          break;
        default:
          break;
      }

      const response = await api.get(`/layout`, {
        params: {
          tabela
        }
      });

      if (response.status === 200) {

        const novosCampos = camposAtuais.map(campo => {
          const campoLista = response.data.DADOS.find((dado: any) => dado?.cam && dado.cam.toLowerCase() === campo.campo.toLowerCase());

          if (campoLista && (campoLista?.vis === '0' || isMobile && campoLista?.vislismob !== '1')) {
            return undefined;
          }

          if (campoLista && campoLista.vis === '1') {
            return {
              ...campo,
              label: campoLista.cap,
              lim: campoLista.lim,
              tipinp: campoLista.tipinp,
              porfic: campoLista.porfic,
              obr: campoLista.obr,
              ordlis: campoLista.ordlis,
              pre: campoLista.pre
            }
          }

          return campo;
        }).sort((campoA: any, campoB: any) => {
          if (campoA?.ordlis && campoB?.ordlis) {
            return Number(campoA?.ordlis) - Number(campoB?.ordlis);
          }
          return 0;
        });

        setCampos(novosCampos.filter(Boolean) as ICampo[]);

      }

    } catch (error) {
      console.log(error);
      setCampos(camposAtuais);
      toast.error(`Erro ao GET /layout. ${entidade}`);
    }
  }

  useEffect(() => {
    if (inputPesquisaRef.current?.value) {
      inputPesquisaRef.current.value = '';
    }
    setPagina(1);

  }, [entidade]);

  useEffect(() => {
    getDados(entidade);
  }, [entidade, codigo, pagina, ordenarPor, porPagina]);

  const titulo = `Cadastro de ${entidade ? entidade.slice(0, 1).toLocaleUpperCase() + entidade.slice(1) : ''}`;

  if (entidade && dados && codigo) {
    return (
      <Pagina
        titulo={titulo}
        subtitulo={codigo === 'novo' ? 'Novo registro' : `Código ${codigo}`}
        acoes={
          <Button
            texto='Voltar'
            variante='secundario'
            icone={<FaArrowLeft />}
            // Volta para a lista da mesma rota (cadastro/... ou comercial/...)
            onClick={() => navigate(location.pathname.replace(/\/[^/]+$/, ''))}
          />
        }
      >
        <FichaCadastroGenerico
          entidade={entidade}
          campos={campos}
          // Mercador não vem propriedade cod no retorno da api, na rota de cadastro produtos o código terá que ser codbar
          // @ts-ignore
          // Com total=0 a API devolve {} (sem data)
          dados={dados.data?.find((dado) => +dado?.cod === Number(codigo) || +dado?.codbar === +Number(codigo)) || {}}
        />
      </Pagina>
    )
  }

  const camposParaPesquisaPlaceHolder = campos.map((campo) => campo?.label).join(', ');

  return (
    <Pagina
      titulo={titulo}
      acoes={
        <>
          <CampoPesquisa
            ref={inputPesquisaRef}
            placeholder={camposParaPesquisaPlaceHolder ? `Pesquisar: ${camposParaPesquisaPlaceHolder}` : 'Pesquisar'}
            onPesquisar={() => getDados(entidade)}
          />
          <Button
            texto={isMobile ? undefined : 'Novo'}
            aria-label='Novo'
            icone={<FaPlus />}
            onClick={() => navigate(`${rotaBase}/cadastro/${entidade}/novo`)}
          />
        </>
      }
    >
      {(entidade && dados) &&
        <ListaCadastroGenerico
          entidade={entidade}
          campos={campos}
          dados={dados.data}
          ordenarPor={ordenarPor}
          setOrdenarPor={setOrdenarPor}
          dadosPaginacao={{
            paginaAtual: pagina,
            totalPaginas: dados.total_paginas,
            setPagina,
            total: dados.total,
            porPagina: usaGrid ? POR_PAGINA_GRID : porPagina,
            ...(!usaGrid && {
              opcoesPorPagina: OPCOES_POR_PAGINA,
              setPorPagina: alterarPorPagina
            })
          }}
        />
      }
    </Pagina>
  );
}
